import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';

interface ReferralSettings {
  creditAmount: number;
  expiryDays: number;
}

export function WalletOpsPage() {
  const [settings, setSettings] = useState<ReferralSettings>({
    creditAmount: 2500,
    expiryDays: 90,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantAmount, setGrantAmount] = useState('2500');
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);
  const [message, setMessage] = useState('');
  const [creditHistory, setCreditHistory] = useState<
    {
      id: string;
      clientEmail: string;
      amountNgn: number;
      source: string;
      expiresAt: string;
      usedAt: string | null;
      createdAt: string;
    }[]
  >([]);
  const [historyEmail, setHistoryEmail] = useState('');

  const loadHistory = (email?: string) => {
    const q = email ? `?email=${encodeURIComponent(email)}` : '';
    adminApi<typeof creditHistory>(`/admin/wallet/credits${q}`)
      .then((data) => setCreditHistory(ensureArray(data)))
      .catch(() => setCreditHistory([]));
  };

  useEffect(() => {
    loadHistory();
    adminApi<ReferralSettings>('/admin/settings/referral')
      .then((data) => {
        if (data.creditAmount) setSettings(data);
      })
      .catch(() => {});
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    setMessage('');
    try {
      await adminApi('/admin/settings/referral', {
        method: 'PATCH',
        body: JSON.stringify(settings),
      });
      setMessage('Referral settings saved.');
    } catch (err) {
      setMessage(
        err instanceof Error && err.message ? err.message : 'Could not save referral settings.'
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const grantCredit = async () => {
    setGranting(true);
    setMessage('');
    try {
      await adminApi('/admin/wallet/grant', {
        method: 'POST',
        body: JSON.stringify({
          clientEmail: grantEmail,
          amountNgn: parseInt(grantAmount, 10),
          note: grantNote || undefined,
        }),
      });
      setMessage(`Granted ₦${parseInt(grantAmount, 10).toLocaleString()} to ${grantEmail}`);
      setGrantEmail('');
      setGrantNote('');
      loadHistory();
    } catch (err) {
      setMessage((err as Error).message || 'Grant failed.');
    } finally {
      setGranting(false);
    }
  };

  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-bold text-gray-900">Wallet & Referrals</h1>
      <p className="mt-1 text-sm text-gray-500">
        Configure referral rewards and grant wallet credit.
      </p>

      {message && (
        <p className="mt-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{message}</p>
      )}

      <div className="card mt-6 space-y-4">
        <h2 className="font-bold text-gray-900">Referral settings</h2>
        <div>
          <label className="label">Credit amount (₦)</label>
          <input
            type="number"
            value={settings.creditAmount}
            onChange={(e) =>
              setSettings((s) => ({ ...s, creditAmount: parseInt(e.target.value, 10) || 0 }))
            }
            className="input-field"
            min={100}
          />
        </div>
        <div>
          <label className="label">Expiry (days)</label>
          <input
            type="number"
            value={settings.expiryDays}
            onChange={(e) =>
              setSettings((s) => ({ ...s, expiryDays: parseInt(e.target.value, 10) || 1 }))
            }
            className="input-field"
            min={1}
            max={365}
          />
        </div>
        <button
          type="button"
          onClick={() => void saveSettings()}
          disabled={savingSettings}
          className="btn-primary"
        >
          {savingSettings ? 'Saving…' : 'Save settings'}
        </button>
      </div>

      <div className="card mt-6 space-y-4">
        <h2 className="font-bold text-gray-900">Grant wallet credit</h2>
        <div>
          <label className="label">Client email</label>
          <input
            type="email"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            className="input-field"
            placeholder="client@example.com"
          />
        </div>
        <div>
          <label className="label">Amount (₦)</label>
          <input
            type="number"
            value={grantAmount}
            onChange={(e) => setGrantAmount(e.target.value)}
            className="input-field"
            min={100}
          />
        </div>
        <div>
          <label className="label">Note (optional)</label>
          <input
            type="text"
            value={grantNote}
            onChange={(e) => setGrantNote(e.target.value)}
            className="input-field"
            maxLength={500}
          />
        </div>
        <button
          type="button"
          onClick={() => void grantCredit()}
          disabled={granting || !grantEmail}
          className="btn-primary"
        >
          {granting ? 'Granting…' : 'Grant credit'}
        </button>
      </div>

      <div className="card mt-6 space-y-4">
        <h2 className="font-bold text-gray-900">Credit history</h2>
        <div className="flex gap-2">
          <input
            type="email"
            value={historyEmail}
            onChange={(e) => setHistoryEmail(e.target.value)}
            className="input-field flex-1"
            placeholder="Filter by client email"
          />
          <button
            type="button"
            onClick={() => loadHistory(historyEmail || undefined)}
            className="btn-secondary"
          >
            Search
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Client</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Source</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {creditHistory.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-gray-400">
                    No credits found.
                  </td>
                </tr>
              ) : (
                creditHistory.map((c) => (
                  <tr key={c.id} className="border-b border-gray-100">
                    <td className="py-2 pr-3">
                      {new Date(c.createdAt).toLocaleDateString('en-NG')}
                    </td>
                    <td className="py-2 pr-3">{c.clientEmail}</td>
                    <td className="py-2 pr-3">₦{c.amountNgn.toLocaleString()}</td>
                    <td className="py-2 pr-3">{c.source}</td>
                    <td className="py-2">
                      {c.usedAt
                        ? 'Used'
                        : new Date(c.expiresAt) < new Date()
                          ? 'Expired'
                          : 'Active'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
